import { Fragment, FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button, Checkbox, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { LastUpdated } from './LastUpdated';
import { useToast } from './Toast';
import { useEntityPermission } from '../lib/usePermissions';
import { latestAttr, type AttrMap } from '../lib/inlineAttribution';

/**
 * ParcelValueExemptionsPanel — the "Exemptions" panel on the parcel value
 * profile. Exemptions are added ON a parcel value record (the generic-CRUD
 * /api/parcel-value-exemptions resource), each targeting land / improvement /
 * both, and flow into the tax calc: business-rules.computeParcelValueExemption
 * sums the CURRENT value row's exemptions into every tax line's exemption_amount
 * (flat `amount` subtracts directly; a `percentage` applies to the target's
 * assessed component), so the estimated tax reflects them.
 *
 * ⚠️ NAMING (part 2 §1.1 / §2). This panel used to read "Reduction" throughout,
 * which is the collision appealstack inherited from Siger: that one word named
 * TWO unrelated things — this record (an exemption/abatement that changes the
 * taxable value) and the BILLING BASIS (the delta between the beginning and
 * settled value, which a fee method takes a percent of). Wiring the first into
 * the billing spine because both were called reduction is what produced phantom
 * tax savings. The record is an **exemption**; the billing basis keeps the name
 * **reduction contingency** and is untouched here. Do not reintroduce the word
 * in this file — the storage layer was already `parcel_value_exemptions`, so only
 * the copy was ever wrong.
 *
 * Three additions on top of the list + add/edit modal + delete:
 *   (b) SHOW THE MATH — an expandable per-row readout of the exemption coming off
 *       BOTH the market value and the assessed value (before → after), computed
 *       client-side the SAME way the backend does.
 *   (c) AUDIT — who last edited the exemption + when, per row, via the shared
 *       field-attribution infra (GET /api/field-attribution/parcel_value_exemptions/:id).
 *   (d) JURISDICTION LINE — an optional "Apply to jurisdiction line" dropdown in
 *       the modal: value-wide (jurisdiction_id null) or scoped to one of the
 *       parcel's jurisdiction lines, so the exemption reduces just that line.
 *
 * (money-blind roles never see `amount`).
 */

export interface ParcelValueExemptionsPanelProps {
  parcelValueId: string;
  /** True only when THIS is the current value row — exemptions on other rows don't move the bill. */
  isCurrentValue?: boolean;
  canEdit: boolean;
  /** Invalidate these query keys after a change so value/parcel/bill totals refresh. */
  invalidateKeys?: unknown[][];
  /** The value's parcel + tax year — source the jurisdiction-line dropdown from this parcel's lines for this year. */
  parcelId: string;
  taxYear: number;
  /** The value's market/assessed figures — the "show the math" before→after and the % base come from these. */
  marketTotal?: number | null;
  assessedTotal?: number | null;
  marketLand?: number | null;
  marketImp?: number | null;
  assessedLand?: number | null;
  assessedImp?: number | null;
}

interface ExemptionRow {
  id: string;
  parcel_value_id: string;
  exemption_type?: string | null;
  target: string;
  amount?: number | null;
  percentage?: number | null;
  is_active?: boolean | number | null;
  jurisdiction_id?: string | null;
  [k: string]: unknown;
}

interface TaxLine {
  id: string;
  jurisdiction_id?: string | null;
  jurisdiction_name?: string | null;
  tax_year?: number | null;
}

// §2 — abatement, freeport, homestead and the rest are TYPES under exemption;
// they do not stand apart as their own object. §3.4: every type is always
// selectable — homestead and over-65 apply directly to any account, so do not
// reintroduce a gate here.
const TYPE_OPTIONS = [
  { value: '', label: '— none —' },
  { value: 'homestead', label: 'Homestead' },
  { value: 'over_65', label: 'Over 65' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'disabled_vet', label: 'Disabled veteran' },
  { value: 'abatement', label: 'Abatement' },
  { value: 'freeport', label: 'Freeport' },
  { value: 'county', label: 'County' },
  { value: 'bpp', label: 'BPP' },
  { value: 'other', label: 'Other' },
];
const TARGET_OPTIONS = [
  { value: 'both', label: 'Both (land + improvement)' },
  { value: 'land', label: 'Land only' },
  { value: 'improvement', label: 'Improvement only' },
];
const TARGET_LABEL: Record<string, string> = { both: 'Both', land: 'Land', improvement: 'Improvement' };

const ALL_LINES = '— All lines (value-wide) —';

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}
function money(v: unknown): string {
  const n = num(v);
  return n == null ? '—' : `$${n.toLocaleString()}`;
}
function pct(v: unknown): string {
  const n = num(v);
  return n == null ? '—' : `${n}%`;
}
function truthy(v: unknown): boolean {
  return v === true || v === 1 || v === '1';
}
// §1.1 — the Type column must never read "Reduction" as a type. Abatement,
// freeport, homestead and the rest are the types; an untyped row falls back to
// the object's own name, not to a word that names a billing concept.
function humanizeType(v?: string | null): string {
  if (!v) return 'Exemption';
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const labelStyle = {
  display: 'block',
  marginBottom: 'var(--sp-1)',
  fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)',
  color: 'var(--color-ink)',
} as const;

interface DraftState {
  id?: string;
  exemption_type: string;
  target: string;
  amount: string;
  percentage: string;
  is_active: boolean;
  /** '' = value-wide (writes jurisdiction_id: null); otherwise a jurisdiction id. */
  jurisdiction_id: string;
}
const EMPTY_DRAFT: DraftState = {
  exemption_type: '', target: 'both', amount: '', percentage: '', is_active: true, jurisdiction_id: '',
};

export function ParcelValueExemptionsPanel({
  parcelValueId,
  isCurrentValue = false,
  canEdit,
  invalidateKeys = [],
  parcelId,
  taxYear,
  marketTotal,
  assessedTotal,
  assessedLand,
  assessedImp,
}: ParcelValueExemptionsPanelProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Remove is a DELETE /api/parcel-value-exemptions/:id → needs parcel_value.can_delete
  // (the add/edit affordances keep the passed-in canEdit = parcel_value.can_edit).
  const canDelete = useEntityPermission('parcel_value', 'can_delete');

  const listQuery = useQuery<ExemptionRow[]>({
    queryKey: ['parcel-value-exemptions', parcelValueId],
    queryFn: ({ signal }) => api.get(`/api/parcel-value-exemptions?parcel_value_id=${parcelValueId}`, signal),
    enabled: !!parcelValueId,
  });
  const rows = listQuery.data ?? [];

  // The parcel's jurisdiction lines for this value's tax year — the source for the
  // "Apply to jurisdiction line" dropdown (tax-records-enriched returns tr.* + jurisdiction_name).
  const linesQuery = useQuery<TaxLine[]>({
    queryKey: ['tax-records-enriched', parcelId],
    queryFn: ({ signal }) => api.get(`/api/tax-records-enriched/${parcelId}`, signal),
    enabled: !!parcelId,
  });

  const jurisdictionOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [{ value: '', label: ALL_LINES }];
    const seen = new Set<string>();
    for (const l of linesQuery.data ?? []) {
      if (l.tax_year != null && Number(l.tax_year) !== Number(taxYear)) continue;
      const jid = l.jurisdiction_id;
      if (!jid || seen.has(jid)) continue;
      seen.add(jid);
      opts.push({ value: jid, label: l.jurisdiction_name || jid });
    }
    return opts;
  }, [linesQuery.data, taxYear]);

  const jurisdictionNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const l of linesQuery.data ?? []) {
      if (l.jurisdiction_id) m[l.jurisdiction_id] = l.jurisdiction_name || l.jurisdiction_id;
    }
    return m;
  }, [linesQuery.data]);

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['parcel-value-exemptions', parcelValueId] });
    for (const key of invalidateKeys) qc.invalidateQueries({ queryKey: key });
  }

  const saveMut = useMutation({
    mutationFn: (d: DraftState) => {
      const body = {
        parcel_value_id: parcelValueId,
        exemption_type: d.exemption_type || null,
        target: d.target,
        amount: d.amount === '' ? null : Number(d.amount),
        percentage: d.percentage === '' ? null : Number(d.percentage),
        is_active: d.is_active,
        // '' → null = value-wide (every line); a set id scopes the exemption to that line.
        jurisdiction_id: d.jurisdiction_id || null,
      };
      return d.id
        ? api.put(`/api/parcel-value-exemptions/${d.id}`, body)
        : api.post('/api/parcel-value-exemptions', body);
    },
    onSuccess: () => {
      invalidateAll();
      setOpen(false);
      setDraft(EMPTY_DRAFT);
      toast({ variant: 'success', message: 'Exemption saved' });
    },
    onError: (e: Error) => toast({ variant: 'error', message: e.message || 'Save failed' }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/parcel-value-exemptions/${id}`),
    onSuccess: () => {
      invalidateAll();
      toast({ variant: 'info', message: 'Exemption removed' });
    },
    onError: (e: Error) => toast({ variant: 'error', message: e.message || 'Delete failed' }),
  });

  function openAdd() {
    setDraft(EMPTY_DRAFT);
    setOpen(true);
  }
  function openEdit(r: ExemptionRow) {
    setDraft({
      id: r.id,
      exemption_type: r.exemption_type ?? '',
      target: r.target || 'both',
      amount: r.amount == null ? '' : String(r.amount),
      percentage: r.percentage == null ? '' : String(r.percentage),
      is_active: truthy(r.is_active),
      jurisdiction_id: r.jurisdiction_id ?? '',
    });
    setOpen(true);
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    saveMut.mutate(draft);
  }
  function toggleRow(id: string) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  // The exemption's applied dollar figure, mirroring business-rules.computeParcelValueExemption:
  // a flat `amount` subtracts directly; a `percentage` applies to the target's assessed component.
  function exemptionValue(r: ExemptionRow): number {
    let v = 0;
    if (r.amount != null) v += Number(r.amount);
    if (r.percentage != null) {
      const base = r.target === 'land' ? assessedLand
        : r.target === 'improvement' ? assessedImp
        : assessedTotal;
      v += (Number(r.percentage) / 100) * Number(base ?? 0);
    }
    return v;
  }

  const colCount = canEdit ? 7 : 6;

  return (
    <div
      style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>Exemptions</h3>
          <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            {isCurrentValue
              ? 'Applied to the tax estimate for this account (current value).'
              : 'This is not the current value row, so these do not affect the live tax estimate.'}
          </p>
        </div>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={openAdd}>+ Add exemption</Button>
        )}
      </div>

      {listQuery.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', margin: 0 }}>No exemptions on this value.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}>
              <th style={{ padding: 'var(--sp-1) var(--sp-2)', width: 28 }}></th>
              <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Type</th>
              <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}>Applies to</th>
              <th style={{ padding: 'var(--sp-1) var(--sp-2)', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: 'var(--sp-1) var(--sp-2)', textAlign: 'right' }}>Percent</th>
              <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}></th>
              {canEdit && <th style={{ padding: 'var(--sp-1) var(--sp-2)' }}></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isOpen = !!expanded[r.id];
              const scopeLabel = r.jurisdiction_id ? (jurisdictionNameById[r.jurisdiction_id] || 'One jurisdiction line') : null;
              return (
                <Fragment key={r.id}>
                  <tr style={{ borderTop: '1px solid var(--color-border)', opacity: truthy(r.is_active) ? 1 : 0.5 }}>
                    <td style={{ padding: 'var(--sp-2)' }}>
                      <button
                        type="button"
                        onClick={() => toggleRow(r.id)}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? 'Hide exemption math' : 'Show exemption math'}
                        style={{
                          border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                          color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', lineHeight: 1,
                        }}
                      >
                        {isOpen ? '▾' : '▸'}
                      </button>
                    </td>
                    <td style={{ padding: 'var(--sp-2)' }}>{humanizeType(r.exemption_type)}</td>
                    <td style={{ padding: 'var(--sp-2)' }}>
                      {TARGET_LABEL[r.target] ?? r.target}
                      {scopeLabel && (
                        <span style={{ color: 'var(--color-ink-3)' }}> · {scopeLabel}</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--sp-2)', textAlign: 'right' }}>{r.amount == null ? '—' : money(r.amount)}</td>
                    <td style={{ padding: 'var(--sp-2)', textAlign: 'right' }}>{r.percentage == null ? '—' : pct(r.percentage)}</td>
                    <td style={{ padding: 'var(--sp-2)' }}>{truthy(r.is_active) ? null : <Badge variant="neutral">Inactive</Badge>}</td>
                    {canEdit && (
                      <td style={{ padding: 'var(--sp-2)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>Edit</Button>
                        {canDelete && (
                          <Button variant="ghost" size="sm" onClick={() => deleteMut.mutate(r.id)} disabled={deleteMut.isPending}>Remove</Button>
                        )}
                      </td>
                    )}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={colCount} style={{ padding: '0 var(--sp-2) var(--sp-3)' }}>
                        <ExemptionMath
                          exemption={exemptionValue(r)}
                          marketTotal={num(marketTotal)}
                          assessedTotal={num(assessedTotal)}
                          scopeLabel={scopeLabel}
                          exemptionId={r.id}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={draft.id ? 'Edit exemption' : 'Add exemption'} size="sm">
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <div>
            <label style={labelStyle}>Type</label>
            <Select
              options={TYPE_OPTIONS}
              value={draft.exemption_type}
              onChange={(e) => setDraft({ ...draft, exemption_type: e.target.value })}
            />
          </div>
          <div>
            <label style={labelStyle}>Applies to</label>
            <Select
              options={TARGET_OPTIONS}
              value={draft.target}
              onChange={(e) => setDraft({ ...draft, target: e.target.value })}
            />
          </div>
          <div>
            <label style={labelStyle}>Apply to jurisdiction line</label>
            <Select
              options={jurisdictionOptions}
              value={draft.jurisdiction_id}
              onChange={(e) => setDraft({ ...draft, jurisdiction_id: e.target.value })}
            />
            <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              Value-wide applies to every tax line; a jurisdiction scopes the exemption to that line only.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Amount ($)</label>
              <Input
                type="number"
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder="flat $"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Percent (%)</label>
              <Input
                type="number"
                inputMode="decimal"
                value={draft.percentage}
                onChange={(e) => setDraft({ ...draft, percentage: e.target.value })}
                placeholder="% of value"
              />
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            A flat amount subtracts directly; a percent applies to the selected component's assessed value.
          </p>
          <Checkbox
            label="Active"
            checked={draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: (e.target as HTMLInputElement).checked })}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm" disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/**
 * The expandable "show the math" area for one exemption row: the exemption's
 * applied dollar figure, then the exemption coming off BOTH the market value and
 * the assessed value (before → after), plus the per-row audit line.
 *
 * §3.6 — an exemption produces NO savings figure. This shows the new values with
 * the exemption applied; it must never render a "saved" number, which is the
 * phantom-savings path the naming split exists to close.
 */
function ExemptionMath({
  exemption, marketTotal, assessedTotal, scopeLabel, exemptionId,
}: {
  exemption: number;
  marketTotal: number | null;
  assessedTotal: number | null;
  scopeLabel: string | null;
  exemptionId: string;
}) {
  const marketAfter = marketTotal == null ? null : marketTotal - exemption;
  const assessedAfter = assessedTotal == null ? null : assessedTotal - exemption;
  return (
    <div
      style={{
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Exemption amount</span>
        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
          − {money(exemption)}
        </span>
      </div>
      <BeforeAfter label="Market value" before={marketTotal} after={marketAfter} />
      <BeforeAfter label="Assessed value" before={assessedTotal} after={assessedAfter} />
      <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        {scopeLabel
          ? `Scoped to the ${scopeLabel} tax line.`
          : 'Applied value-wide to every tax line.'}
      </p>
      <ExemptionAudit exemptionId={exemptionId} />
    </div>
  );
}

function BeforeAfter({ label, before, after }: { label: string; before: number | null; after: number | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', fontSize: 'var(--fs-sm)' }}>
      <span style={{ color: 'var(--color-ink-2)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)' }}>
        {money(before)} <span style={{ color: 'var(--color-ink-3)' }}>→</span>{' '}
        <span style={{ fontWeight: 'var(--fw-semibold)' }}>{money(after)}</span>
      </span>
    </div>
  );
}

/**
 * Per-row audit — who last edited this exemption + when, via the shared
 * field-attribution infra (GET /api/field-attribution/parcel_value_exemptions/:id
 * → { <field>: { by, at } }). Generic-CRUD writes to this table are audited, so an
 * exemption that has been edited resolves attribution here (the same pattern the
 * parcel-value inline cards use). Mounts lazily — only when a row is expanded.
 */
function ExemptionAudit({ exemptionId }: { exemptionId: string }) {
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'parcel_value_exemptions', exemptionId],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/parcel_value_exemptions/${exemptionId}`, signal),
  });
  const attr = attrQuery.data ?? {};
  const latest = latestAttr(attr, Object.keys(attr));
  if (!latest) {
    return (
      <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        No edits recorded yet.
      </p>
    );
  }
  return <LastUpdated updatedBy={latest.by} updatedAt={latest.at} />;
}
